import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { combineReducers } from 'redux';
import { createRoutesWithSaveInProgress } from 'platform/forms/save-in-progress/helpers';
import { toggleValues } from 'platform/site-wide/feature-toggles/selectors';
import { isLoggedIn } from 'platform/user/selectors';
import { commonReducer } from 'platform/startup/store';

import Form526EZApp from './Form526EZApp';
import formConfig from './config/form';
import { GET_STARTED_PATH, START_PATH } from './constants';
import { showSubTaskStartFlow } from './utils/startFlow';
import WizardContainer from './containers/WizardContainer';
import SubTaskContainer from './subtask/SubTaskContainer';
import FormVersionShell from './FormVersionShell';
import v1AppReducers from './reducers';
import { selectFormVersion } from './versioning';

export { showSubTaskStartFlow };

export const requiresAuthForScreener = state => {
  const toggles = toggleValues(state);
  return toggles.disability_526_new_screener_question_flow === true;
};

export const StartFlowContainer = ({
  isUserLoggedIn,
  location,
  requiresAuth,
  router,
  togglesLoading,
  userProfileLoading,
  useSubTaskPattern,
}) => {
  const shouldRedirect =
    !togglesLoading && !userProfileLoading && requiresAuth && !isUserLoggedIn;

  // /start and /get-started both render this container; send the Veteran to
  // whichever one matches the flow the toggle selected so the URL, breadcrumb,
  // and heading always agree.
  const canonicalPath = useSubTaskPattern ? GET_STARTED_PATH : START_PATH;
  const pathname = location?.pathname?.replace(/\/+$/, '') || '';
  const needsCanonicalPath =
    !togglesLoading &&
    !userProfileLoading &&
    !shouldRedirect &&
    !!pathname &&
    !pathname.endsWith(canonicalPath);

  React.useEffect(() => {
    if (shouldRedirect) {
      router?.replace('/introduction');
    }
  }, [shouldRedirect, router]);

  React.useEffect(() => {
    if (needsCanonicalPath) {
      router?.replace(canonicalPath);
    }
  }, [needsCanonicalPath, canonicalPath, router]);

  if (
    togglesLoading ||
    userProfileLoading ||
    shouldRedirect ||
    needsCanonicalPath
  ) {
    return (
      <va-loading-indicator message="Please wait while we load the application for you." />
    );
  }

  return useSubTaskPattern ? <SubTaskContainer /> : <WizardContainer />;
};

StartFlowContainer.propTypes = {
  isUserLoggedIn: PropTypes.bool,
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }),
  requiresAuth: PropTypes.bool,
  router: PropTypes.shape({
    replace: PropTypes.func,
  }),
  togglesLoading: PropTypes.bool,
  useSubTaskPattern: PropTypes.bool,
  userProfileLoading: PropTypes.bool,
};

const mapStateToProps = state => ({
  isUserLoggedIn: isLoggedIn(state),
  requiresAuth: requiresAuthForScreener(state),
  togglesLoading: state?.featureToggles?.loading,
  userProfileLoading: state?.user?.profile?.loading,
  useSubTaskPattern: showSubTaskStartFlow(state),
});

const ConnectedStartFlowContainer =
  connect(mapStateToProps)(StartFlowContainer);

// Both paths resolve to the same container, which redirects to the canonical
// one for the active flow.
const startFlowRoutes = [START_PATH, GET_STARTED_PATH].map(path => ({
  path,
  component: ConnectedStartFlowContainer,
}));

// Static v1-only tree; kept as the default export for the routes unit tests.
// The app mounts the versioned tree via createFormRoutes below.
const v1RootRoute = {
  path: '/',
  component: Form526EZApp,
  indexRoute: {
    onEnter: (nextState, replace) => replace('/introduction'),
  },
  childRoutes: createRoutesWithSaveInProgress(formConfig),
};

const routes = [...startFlowRoutes, v1RootRoute];

export default routes;

// ---------------------------------------------------------------------------
// Single-app (client-side version switch) routing
// ---------------------------------------------------------------------------
// Both 526 flows are served from this one bundle, chosen at runtime (flag +
// saved forms) and mounted in place — no cross-bundle nav, so the va.gov shell
// is never torn down.

// v1 subtree nested under the version shell (pathless: adds no URL segment).
// Takes the store to undo buildV2Subtree's reducer swap if a restart lands
// back on v1 mid-session.
const buildV1Subtree = store => {
  const v1FormId = formConfig.formId;
  const v1FormReducer = v1AppReducers.form;
  const currentFormId = store?.getState()?.form?.formId;

  if (currentFormId && currentFormId !== v1FormId) {
    const reinitializingFormReducer = (state, action) =>
      state && state.formId !== v1FormId
        ? v1FormReducer(undefined, action)
        : v1FormReducer(state, action);

    store.replaceReducer(
      combineReducers({
        ...v1AppReducers,
        form: reinitializingFormReducer,
        ...commonReducer,
      }),
    );
  }

  return [
    {
      component: Form526EZApp,
      indexRoute: {
        onEnter: (nextState, replace) => replace('/introduction'),
      },
      childRoutes: createRoutesWithSaveInProgress(formConfig),
    },
  ];
};

// v2 subtree. All v2 code is dynamically imported so v1 users never download it,
// and the matching v2 form reducer is swapped in before the routes mount.
const buildV2Subtree = async store => {
  const [
    { default: v2FormConfig },
    { default: v2AppReducers },
    { default: AllClaimsV2App },
  ] = await Promise.all([
    import('../all-claims-v2/config/form'),
    import('../all-claims-v2/reducers'),
    import('../all-claims-v2/components/AllClaimsV2App'),
  ]);

  // The `form` slice was initialized under v1, so reinitialize it to v2's
  // initial state on swap — otherwise v2 save-in-progress would persist under
  // the v1 formId. Branch on formId rather than a one-shot boolean: combineReducers
  // calls the reducer with `undefined` state to assert its shape before the real
  // REPLACE dispatch, which would consume a one-shot flag; keying off formId is
  // idempotent across repeat REPLACE dispatches.
  const v2FormReducer = v2AppReducers.form;
  const v2FormId = v2FormConfig.formId;
  const reinitializingFormReducer = (state, action) =>
    state && state.formId !== v2FormId
      ? v2FormReducer(undefined, action)
      : v2FormReducer(state, action);

  store.replaceReducer(
    combineReducers({
      ...v1AppReducers,
      ...v2AppReducers,
      form: reinitializingFormReducer,
      ...commonReducer,
    }),
  );

  return [
    {
      component: AllClaimsV2App,
      indexRoute: {
        onEnter: (nextState, replace) => replace('/introduction'),
      },
      childRoutes: createRoutesWithSaveInProgress(v2FormConfig),
    },
  ];
};

// Resolve the version subtree once the store has enough state to decide.
// Resolves { version, childRoutes } so callers can detect a later re-decision.
export const resolveVersionRoutes = store =>
  new Promise((resolve, reject) => {
    let settled = false;

    const attempt = () => {
      if (settled) return true;
      const state = store.getState();
      if (state?.featureToggles?.loading) return false;

      const version = selectFormVersion(state);
      if (!version) return false; // wait for profile/savedForms to settle

      settled = true;
      const built =
        version === 'v2'
          ? buildV2Subtree(store)
          : Promise.resolve(buildV1Subtree(store));
      built
        .then(childRoutes => resolve({ version, childRoutes }))
        .catch(reject);
      return true;
    };

    if (attempt()) return;
    const unsubscribe = store.subscribe(() => {
      if (attempt()) unsubscribe();
    });
  });

const versionedRootRoute = store => {
  // RRv3 calls getChildRoutes on every transition into '/'. Resolve the version
  // subtree once and return it SYNCHRONOUSLY after — re-resolving on every call
  // remounts the form subtree and resets in-progress routing.
  let cachedChildRoutes;
  let cachedVersion;
  let childRoutesPromise;

  // Continue stays on the cache since selectFormVersion still agrees with it.
  // A restart can make the live decision disagree (e.g. deleting the active
  // v2 draft falls back to v1), so invalidate by comparing outcomes rather
  // than watching isStartingOver, which never resets to false after a restart.
  const invalidateIfVersionChanged = () => {
    if (!cachedVersion) return; // nothing resolved yet to compare against
    const nextVersion = selectFormVersion(store.getState());
    if (nextVersion && nextVersion !== cachedVersion) {
      cachedChildRoutes = undefined;
      cachedVersion = undefined;
      childRoutesPromise = undefined;
    }
  };
  store.subscribe(invalidateIfVersionChanged);

  // Named so getChildRoutes can stamp resolvedVersion onto this same object,
  // which RRv3 injects as `props.route` into FormVersionShell.
  const route = {
    path: '/',
    component: FormVersionShell,
    indexRoute: {
      onEnter: (nextState, replace) => replace('/introduction'),
    },
    getChildRoutes(partialNextState, callback) {
      invalidateIfVersionChanged();
      if (cachedChildRoutes) {
        callback(null, cachedChildRoutes);
        return;
      }
      if (!childRoutesPromise) {
        childRoutesPromise = resolveVersionRoutes(store);
      }
      childRoutesPromise
        .then(({ version, childRoutes }) => {
          cachedVersion = version;
          route.resolvedVersion = version;
          cachedChildRoutes = childRoutes;
          callback(null, childRoutes);
        })
        .catch(err => callback(err));
    },
  };

  return route;
};

// Route factory used with startApp({ createRoutesWithStore }). Needs the store
// so the version decision can read live Redux state.
export const createFormRoutes = store => [
  ...startFlowRoutes,
  versionedRootRoute(store),
];
